import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AdminShell } from "../../components/AdminShell";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { api, erroMsg } from "../../lib/api";
import { dataCurta } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import { KeyRound, Loader2, Pencil, Plus, Trash2 } from "lucide-react";

const vazio = {
  nome: "",
  email: "",
  senha: "",
  role: "representante",
  telefone: "",
  regiao: "",
  ativo: true,
};

export default function AdminUsuarios() {
  const { user: eu } = useAuth();
  const [itens, setItens] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [aberto, setAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(vazio);
  const [salvando, setSalvando] = useState(false);
  const [excluir, setExcluir] = useState(null);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const r = await api.get("/admin/users");
      setItens(r.data.itens);
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const salvar = async () => {
    if (!form.nome.trim() || !form.email.trim()) {
      toast.error("Informe nome e e-mail.");
      return;
    }
    if (!editando && form.senha.length < 4) {
      toast.error("Defina uma senha com pelo menos 4 caracteres.");
      return;
    }
    setSalvando(true);
    try {
      if (editando) {
        const payload = { ...form };
        if (!payload.senha) delete payload.senha;
        await api.put(`/admin/users/${editando.id}`, payload);
        toast.success("Usuário atualizado");
      } else {
        await api.post("/admin/users", form);
        toast.success("Usuário criado — envie o acesso ao representante");
      }
      setAberto(false);
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtivo = async (u, valor) => {
    try {
      await api.put(`/admin/users/${u.id}`, { ativo: valor });
      toast.success(valor ? "Usuário ativado" : "Usuário desativado");
      carregar();
    } catch (e) {
      toast.error(erroMsg(e));
      carregar();
    }
  };

  return (
    <AdminShell
      titulo="Usuários"
      descricao="Crie e gerencie os acessos dos representantes comerciais."
      acoes={
        <Button
          onClick={() => {
            setEditando(null);
            setForm(vazio);
            setAberto(true);
          }}
          data-testid="admin-usuarios-novo-button"
        >
          <Plus className="mr-1.5 h-4 w-4" /> Novo usuário
        </Button>
      }
    >
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
        <div className="overflow-x-auto">
          <Table data-testid="admin-usuarios-table">
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead className="w-[130px]">Perfil</TableHead>
                <TableHead className="w-[140px]">Região</TableHead>
                <TableHead className="w-[130px]">Telefone</TableHead>
                <TableHead className="w-[110px]">Criado</TableHead>
                <TableHead className="w-[90px]">Ativo</TableHead>
                <TableHead className="w-[100px] text-right">Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {carregando ? (
                <TableRow>
                  <TableCell colSpan={8} className="py-8 text-center">
                    <Loader2 className="mx-auto h-5 w-5 animate-spin text-primary" />
                  </TableCell>
                </TableRow>
              ) : (
                itens.map((u) => (
                  <TableRow key={u.id} data-testid={`admin-usuario-row-${u.email}`}>
                    <TableCell className="text-[13px] font-medium text-slate-900">{u.nome}</TableCell>
                    <TableCell className="text-[12.5px] text-slate-600">{u.email}</TableCell>
                    <TableCell>
                      <Badge
                        variant={u.role === "admin" ? "default" : "secondary"}
                        className={u.role === "admin" ? "bg-slate-900 text-white" : ""}
                      >
                        {u.role === "admin" ? "Administrador" : "Representante"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-[12.5px]">{u.regiao || "—"}</TableCell>
                    <TableCell className="text-[12.5px]">{u.telefone || "—"}</TableCell>
                    <TableCell className="text-[12px] text-slate-500">
                      {dataCurta(u.criado_em)}
                    </TableCell>
                    <TableCell>
                      <Switch
                        checked={u.ativo}
                        onCheckedChange={(v) => alternarAtivo(u, v)}
                        disabled={u.id === eu?.id}
                        data-testid={`admin-usuarios-status-switch-${u.email}`}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => {
                            setEditando(u);
                            setForm({ ...vazio, ...u, senha: "" });
                            setAberto(true);
                          }}
                          data-testid={`admin-usuario-editar-${u.email}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-slate-400 hover:text-destructive"
                          disabled={u.id === eu?.id}
                          onClick={() => setExcluir(u)}
                          data-testid={`admin-usuario-excluir-${u.email}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <Sheet open={aberto} onOpenChange={setAberto}>
        <SheetContent className="overflow-y-auto bg-white sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>{editando ? "Editar usuário" : "Novo usuário"}</SheetTitle>
            <SheetDescription>
              O representante entra no portal com o e-mail e a senha definidos aqui.
            </SheetDescription>
          </SheetHeader>
          <div className="mt-5 space-y-4 pb-6">
            <div className="space-y-1.5">
              <Label>Nome completo *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({ ...form, nome: e.target.value })}
                className="h-10"
                data-testid="admin-usuario-nome"
              />
            </div>
            <div className="space-y-1.5">
              <Label>E-mail *</Label>
              <Input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="h-10"
                data-testid="admin-usuario-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label>{editando ? "Nova senha (deixe vazio para manter)" : "Senha *"}</Label>
              <Input
                type="text"
                value={form.senha}
                onChange={(e) => setForm({ ...form, senha: e.target.value })}
                placeholder={editando ? "••••••••" : "Defina a senha inicial"}
                className="h-10"
                data-testid="admin-usuario-senha"
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Perfil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="h-10" data-testid="admin-usuario-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="representante">Representante</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Região / Estado</Label>
                <Input
                  value={form.regiao}
                  onChange={(e) => setForm({ ...form, regiao: e.target.value })}
                  placeholder="Ex.: Nordeste"
                  className="h-10"
                  data-testid="admin-usuario-regiao"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Telefone</Label>
              <Input
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                placeholder="(00) 00000-0000"
                className="h-10"
                data-testid="admin-usuario-telefone"
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <div className="text-[13px] font-medium text-slate-900">Acesso ativo</div>
                <div className="text-[11.5px] text-slate-500">
                  Usuários inativos não conseguem entrar no portal.
                </div>
              </div>
              <Switch
                checked={form.ativo}
                onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                data-testid="admin-usuario-ativo"
              />
            </div>

            <div className="flex items-start gap-2 rounded-lg bg-slate-50 px-3 py-2.5 text-[11.5px] text-slate-600">
              <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
              Envie o e-mail e a senha ao representante. Ele poderá trocá-la em “Minha conta”.
            </div>

            <div className="flex gap-2">
              <Button className="h-11 flex-1" onClick={salvar} disabled={salvando} data-testid="admin-usuario-salvar">
                {salvando && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {editando ? "Salvar alterações" : "Criar usuário"}
              </Button>
              <Button variant="outline" className="h-11" onClick={() => setAberto(false)}>
                Cancelar
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={!!excluir} onOpenChange={(o) => !o && setExcluir(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir usuário?</AlertDialogTitle>
            <AlertDialogDescription>
              {excluir?.nome} ({excluir?.email}) perderá o acesso imediatamente. Orçamentos já
              criados permanecem no histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={async () => {
                try {
                  await api.delete(`/admin/users/${excluir.id}`);
                  toast.success("Usuário excluído");
                  setExcluir(null);
                  carregar();
                } catch (e) {
                  toast.error(erroMsg(e));
                }
              }}
              data-testid="admin-usuario-excluir-confirmar"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminShell>
  );
}
